import { Fragment, FunctionComponent, ReactNode, useEffect, useRef } from 'react'
import { NextComponentType } from 'next'
import App, { AppContext, AppProps as NextAppProps } from 'next/app'
import { useRouter } from 'next/router'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { flowRight as compose } from 'lodash'
import { DefaultSeo } from 'next-seo'

import { ThemeProvider } from '@/lib/styles'
import withAuth from '@/lib/auth/withAuth'
import { ErrorProvider } from '@/lib/error'
import { withResponsiveProvider } from '@/lib/responsive'
import { UTMContextProvider } from '@/lib/gtm/utmContext'
import { useDisclosure } from '@chakra-ui/react'

import { MAINTENANCE_PATH } from '../components/MaintenanceModal/constants'
import { MaintenanceModal } from '@/components/MaintenanceModal'

import envObj from '@/config/env'

import { DEFAULT_SEO_CONFIG } from '../constants/seo'

const Noop = ({ children }: { children: ReactNode }) => children

type PageComponentType = NextComponentType & {
  Layout?: FunctionComponent
}

type AppProps = NextAppProps & PageProps

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      retry: false,
      refetchOnWindowFocus: false,
      useErrorBoundary: true,
    },
  },
})

function MyApp(props: AppProps): ReactNode {
  const { router, userInfo } = props
  const Component: PageComponentType = props.Component
  const ssPageProps = { ...props.pageProps, router, userInfo }

  const Layout = (Component.Layout || Noop) as FunctionComponent

  const disclosure = useDisclosure()
  const { pathname } = useRouter()
  const hadShownMaintenanceModal = useRef(false)

  useEffect(() => {
    if (hadShownMaintenanceModal.current) return
    if (!MAINTENANCE_PATH.includes(pathname)) return

    const timer = setTimeout(() => {
      disclosure.onOpen()
      hadShownMaintenanceModal.current = true
    }, 500)

    return () => clearTimeout(timer)
  }, [pathname])

  useEffect(() => {
    if (window.howuku && userInfo) {
      window.howuku.identify({
        email: userInfo.email,
        company: userInfo.company_name ?? '',
      })
    }
  }, [userInfo?.email])

  const url = `${envObj.APP_URL}${router.asPath}`

  return (
    <Fragment>
      <DefaultSeo
        {...DEFAULT_SEO_CONFIG}
        openGraph={{
          ...DEFAULT_SEO_CONFIG.openGraph,
          url: url,
        }}
        canonical={url}
      />

      <UTMContextProvider>
        <ThemeProvider>
          <ErrorProvider>
            <QueryClientProvider client={queryClient}>
              <Layout {...ssPageProps}>
                <Component {...ssPageProps} />
                <MaintenanceModal disclosure={disclosure} />
              </Layout>

              <ReactQueryDevtools
                position="bottom-right"
                initialIsOpen={false}
              />
            </QueryClientProvider>
          </ErrorProvider>
        </ThemeProvider>
      </UTMContextProvider>
    </Fragment>
  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext)

  return { ...appProps }
}

export default compose(withAuth, withResponsiveProvider)(MyApp)
